import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  endOfQuarter,
  format,
  isWithinInterval,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from "date-fns";
import { Send, Trophy, TrendingUp, Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type RangeKey = "all" | "this_quarter" | "last_quarter";
type ChapterKey = "all" | "washington" | "union";

interface Referral {
  id: string;
  referred_by_id: string;
  referred_to_id: string;
  status: string;
  chapter: string;
  referral_date: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "hsl(45 93% 58%)",
  contacted: "hsl(217 91% 60%)",
  closed_won: "hsl(142 71% 45%)",
  closed_lost: "hsl(0 72% 51%)",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  contacted: "Contacted",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

const AdminReferralStats = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [members, setMembers] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<RangeKey>("all");
  const [chapter, setChapter] = useState<ChapterKey>("all");

  useEffect(() => {
    const load = async () => {
      const { data: refs } = await supabase
        .from("referrals")
        .select("id, referred_by_id, referred_to_id, status, chapter, referral_date");

      const { data: mems } = await supabase.from("members").select("id, name");

      setReferrals(refs || []);
      setMembers(new Map((mems || []).map((m) => [m.id, m.name])));
      setLoading(false);
    };
    load();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    let windowStart: Date | null = null;
    let windowEnd: Date | null = null;
    if (range === "this_quarter") {
      windowStart = startOfQuarter(now);
      windowEnd = endOfQuarter(now);
    } else if (range === "last_quarter") {
      const lastQ = subQuarters(now, 1);
      windowStart = startOfQuarter(lastQ);
      windowEnd = endOfQuarter(lastQ);
    }

    const filtered = referrals.filter((r) => {
      if (chapter !== "all" && r.chapter !== chapter) return false;
      if (windowStart && windowEnd) {
        const d = new Date(r.referral_date + "T00:00:00");
        if (!isWithinInterval(d, { start: windowStart, end: windowEnd })) return false;
      }
      return true;
    });

    const total = filtered.length;
    const won = filtered.filter((r) => r.status === "closed_won").length;
    const conversion = total > 0 ? Math.round((won / total) * 100) : 0;
    const activeReferrers = new Set(filtered.map((r) => r.referred_by_id)).size;

    // Monthly trend (last 12 months) by chapter
    const months: {
      key: string;
      month: string;
      washington: number;
      union: number;
      total: number;
    }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = startOfMonth(subMonths(new Date(), i));
      months.push({
        key: format(d, "yyyy-MM"),
        month: format(d, "MMM yy"),
        washington: 0,
        union: 0,
        total: 0,
      });
    }
    const monthMap = new Map(months.map((m) => [m.key, m]));
    filtered.forEach((r) => {
      const bucket = monthMap.get(format(new Date(r.referral_date + "T00:00:00"), "yyyy-MM"));
      if (!bucket) return;
      bucket.total++;
      if (r.chapter === "washington") bucket.washington++;
      else if (r.chapter === "union") bucket.union++;
    });

    // Status breakdown
    const statusCounts = filtered.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
    const statusBreakdown = Object.entries(statusCounts).map(([key, value]) => ({
      key,
      name: STATUS_LABELS[key] || key,
      value,
    }));

    // Top 5 senders
    const senderCounts = filtered.reduce<Record<string, number>>((acc, r) => {
      acc[r.referred_by_id] = (acc[r.referred_by_id] || 0) + 1;
      return acc;
    }, {});
    const topSenders = Object.entries(senderCounts)
      .map(([id, count]) => ({ name: members.get(id) || "Unknown", count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      total,
      won,
      conversion,
      activeReferrers,
      months: months.map(({ key, ...rest }) => rest),
      statusBreakdown,
      topSenders,
    };
  }, [referrals, members, range, chapter]);

  const trendConfig: ChartConfig = {
    washington: { label: "Washington", color: "hsl(217 91% 60%)" },
    union: { label: "Union", color: "hsl(142 71% 45%)" },
  };

  const statusConfig: ChartConfig = stats.statusBreakdown.reduce((acc, s) => {
    acc[s.key] = { label: s.name, color: STATUS_COLORS[s.key] || "hsl(var(--muted))" };
    return acc;
  }, {} as ChartConfig);

  const sendersConfig: ChartConfig = {
    count: { label: "Referrals", color: "hsl(var(--primary))" },
  };

  if (loading) {
    return <p className="text-center py-8 text-muted-foreground">Loading reports...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold">Referral Reports</h2>
        <div className="flex flex-wrap gap-2">
          <Select value={chapter} onValueChange={(v) => setChapter(v as ChapterKey)}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Chapters</SelectItem>
              <SelectItem value="washington">Washington</SelectItem>
              <SelectItem value="union">Union</SelectItem>
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Send className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Closed Won</p>
                <p className="text-2xl font-bold">{stats.won}</p>
              </div>
              <Trophy className="h-5 w-5 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Win Rate</p>
                <p className="text-2xl font-bold">{stats.conversion}%</p>
              </div>
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Referrers</p>
                <p className="text-2xl font-bold">{stats.activeReferrers}</p>
              </div>
              <Users className="h-5 w-5 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Referrals by month (last 12 months)</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={trendConfig} className="h-[280px] w-full">
            <LineChart data={stats.months}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line
                type="monotone"
                dataKey="washington"
                stroke="var(--color-washington)"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="union"
                stroke="var(--color-union)"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.statusBreakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No referrals yet.</p>
            ) : (
              <>
                <ChartContainer config={statusConfig} className="h-[240px] w-full">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                    <Pie
                      data={stats.statusBreakdown}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {stats.statusBreakdown.map((entry) => (
                        <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {stats.statusBreakdown.map((s) => (
                    <div key={s.key} className="flex items-center gap-1.5 text-xs">
                      <span
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: STATUS_COLORS[s.key] }}
                      />
                      <span className="text-muted-foreground">
                        {s.name} ({s.value})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top 5 referrers</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.topSenders.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No referrals yet.</p>
            ) : (
              <ChartContainer config={sendersConfig} className="h-[240px] w-full">
                <BarChart data={stats.topSenders} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    width={110}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReferralStats;
